import React from "react"
import {
  Grid,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import { motion } from "framer-motion"

interface ImageDisplayProps {
  imageUrl: string
  altText: string
  title?: string | null
  description?: string | null
  hasPadding: boolean
  hasBottomBorder: boolean
  backgroundColor?: string
}

interface ImgScrollWrapperProps {
  hasPadding: boolean
  hasBottomBorder: boolean
}

const ImgScrollWrapper = styled("div")<ImgScrollWrapperProps>(
  ({ theme, hasPadding, hasBottomBorder }) => ({
    width: "100%",
    overflow: "hidden",
    position: "relative", // IMPORTANT
    paddingTop: hasPadding ? theme.spacing(16) : 0,
    paddingBottom: hasPadding ? theme.spacing(16) : 0,
    display: "flex",
    alignItems: "stretch",

    [theme.breakpoints.down("lg")]: {
      paddingTop: hasPadding ? theme.spacing(10) : 0,
      paddingBottom: hasPadding ? theme.spacing(10) : 0,
      borderBottom: hasBottomBorder ? "32px solid white" : "none",
    },

    [theme.breakpoints.down("md")]: {
      paddingTop: hasPadding ? theme.spacing(6) : 0,
      paddingBottom: hasPadding ? theme.spacing(6) : 0,
      borderBottom: hasBottomBorder ? "32px solid white" : "none",
    },
  }),
)

const CaptionOverlay = styled("div")(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(2),
  left: theme.spacing(2),
  zIndex: 10,
  padding: theme.spacing(0, 0.5),
  borderRadius: theme.spacing(0),
  backgroundColor: "white",
  color: "black",
  maxWidth: "70%",
  backdropFilter: "blur(6px)",
  [theme.breakpoints.up("sm")]: {
    top: theme.spacing(8),
    left: theme.spacing(8),
  },
}))

const WorkDetailImage: React.FC<ImageDisplayProps> = ({
  imageUrl,
  altText,
  title,
  description,
  hasPadding,
  hasBottomBorder,
  backgroundColor,
}) => {
  const theme = useTheme()
  const isXSmall = useMediaQuery(theme.breakpoints.only("xs"))
  const isSmall = useMediaQuery(theme.breakpoints.only("sm"))
  const isMedium = useMediaQuery(theme.breakpoints.only("md"))
  const isLarge = useMediaQuery(theme.breakpoints.only("lg"))

  const imageSizeMap = {
    xsmall: 750,
    small: 750,
    medium: 850,
    large: 1103,
    xlarge: 1600,
  } as const

  const screenSize = isXSmall
    ? "xsmall"
    : isSmall
      ? "small"
      : isMedium
        ? "medium"
        : isLarge
          ? "large"
          : "xlarge"

  const resizedImageUrl = `${imageUrl}?w=${imageSizeMap[screenSize]}`

  const descriptionText = (description ?? "").trim()
  const showCaption = Boolean(descriptionText)

  return (
    <Grid item lg={12} sx={{ width: "100%", backgroundColor }}>
      <ImgScrollWrapper
        hasPadding={hasPadding}
        hasBottomBorder={hasBottomBorder}
      >
        {showCaption && (
          <CaptionOverlay>
            <Typography
              variant="caption"
              fontWeight={800}
              textTransform="uppercase"
            >
              {descriptionText}
            </Typography>
          </CaptionOverlay>
        )}

        <motion.img
          src={resizedImageUrl}
          alt={altText}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
      </ImgScrollWrapper>
    </Grid>
  )
}

export default WorkDetailImage
